// src/components/dashboards/add_patient_v2/dnd/dndShared.ts
// -----------------------------------------------------------------------------
// Shared primitives for the drag-and-drop strategies (Joan, 2026-05-27).
//
// SCOPE, 2026-09-09 (cursor_logs/075 §4.2): drops are handled by react-dropzone
// ("Strategy 6" in AddPatientFormV2). What survives here is the HIGHLIGHT layer
// only: zoneElForTarget + makeHighlighter, and the helpers they use internally.
// `routeDrop` (the last caller of the hand-rolled readDataTransfer) and
// `dragHasFiles` were removed as unreachable. The three rules below still
// describe why the highlight layer touches no React state.
//
// Every strategy MUST obey the three rules the research surfaced:
//   1. preventDefault UNCONDITIONALLY on dragenter + dragover — never gate it
//      on dataTransfer.types (Linux/Chrome/Firefox omit "Files" mid-drag, and
//      a single skipped preventDefault silently kills the drop). [MDN]
//   2. NO React setState during a drag — re-rendering the drop target detaches
//      it (flaky drops) and repaints the backdrop-filter (lag). Highlight is
//      done with a DOM outline only. [react-dropzone#1035, Smashing]
//   3. Extract webkitGetAsEntry()/getAsFile() SYNCHRONOUSLY in the drop
//      handler before any await — the items list empties afterwards. [MDN]
// -----------------------------------------------------------------------------


export const CARD_ATTR = "data-patient-card-id";
export const BULK_ATTR = "data-bulk-dropzone";

/** Closest card element for an event target, or null. */
export function cardElForTarget(target: EventTarget | null): HTMLElement | null {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return null;
  return el.closest(`[${CARD_ATTR}]`) as HTMLElement | null;
}

/** The zone element to HIGHLIGHT for a given target: the card under the
 *  pointer, else the bulk dropzone (when inside the form), else null. */
export function zoneElForTarget(
  target: EventTarget | null,
  formRoot: HTMLElement | null,
): HTMLElement | null {
  const card = cardElForTarget(target);
  if (card) return card;
  if (formRoot && target instanceof Node && formRoot.contains(target)) {
    return formRoot.querySelector(`[${BULK_ATTR}]`) as HTMLElement | null;
  }
  return null;
}

// ── DOM-only highlight (no React re-render) ─────────────────────────────────
// A 2px dashed outline drawn OUTSIDE the box — composited, no layout, no
// backdrop-filter repaint. We stash/restore the previous inline value.
const HL_OUTLINE = "2px dashed var(--dnd-accent, #2f6df6)";
const HL_OFFSET = "2px";

export function applyHighlight(el: HTMLElement | null): void {
  if (!el) return;
  el.style.outline = HL_OUTLINE;
  el.style.outlineOffset = HL_OFFSET;
  el.style.borderRadius = el.style.borderRadius || "";
}

export function clearHighlight(el: HTMLElement | null): void {
  if (!el) return;
  el.style.outline = "";
  el.style.outlineOffset = "";
}

/** Tracks a single highlighted element and swaps it without React. */
export function makeHighlighter() {
  let current: HTMLElement | null = null;
  return {
    set(el: HTMLElement | null) {
      if (current === el) return;
      clearHighlight(current);
      current = el;
      applyHighlight(current);
    },
    clear() {
      clearHighlight(current);
      current = null;
    },
  };
}

